/**
 * ApiKeysSettings Component
 *
 * API Keys tab content.
 * Displays list of API keys with ability to generate new ones, edit their roles and revoke them.
 */
import { useQuery } from '@apollo/client/react';
import { useState, useEffect } from 'react';
import { Paper, Button } from '@insio/ui-toolkit';
import { RolesGuide } from '@/components/roles-guide/RolesGuide';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { GetApiKeysDocument } from '@/graphql';
import {
  ApiKeysTable,
  GenerateApiKeyModal,
  ApiKeyDisplayModal,
  EditApiKeyRolesModal
} from './api-keys';
import { ApiKeyCreated, type ApiKeyFieldsFragment } from '@/graphql';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { getConfig } from '@/config';
import { KEY_ASSIGNABLE_ROLES } from '../../constants/roles';

export function ApiKeysSettings() {
  const t = useFeatureTranslation('systemSettings.apiKeys');
  const tRole = useEnumTranslation('userRole');
  const tRoleDescription = useEnumTranslation('userRoleDescription');
  const { showSuccess, showError } = useToastHelpers();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<ApiKeyCreated | null>(null);
  const [editingKey, setEditingKey] = useState<ApiKeyFieldsFragment | null>(null);

  // Fetch API keys
  const { data, loading, error, refetch } = useQuery(GetApiKeysDocument, {
    fetchPolicy: 'network-only'
  });
  const showLoading = useDelayedLoading(loading);

  // Show error toast when query fails
  useEffect(() => {
    if (error) {
      showError(t('errors.loadError'));
    }
  }, [error, showError, t]);

  const handleGenerateSuccess = (key: ApiKeyCreated) => {
    setGeneratedKey(key);
    setShowGenerateModal(false);
    showSuccess(t('success.generateSuccess'));
  };

  const handleCloseGeneratedKeyModal = () => {
    setGeneratedKey(null);
    refetch();
  };

  const handleEditRolesSuccess = () => {
    setEditingKey(null);
    showSuccess(t('success.rolesUpdated'));
    refetch();
  };

  /** The API documentation is a separate site, so it opens in its own tab. */
  const openApiDocs = () => {
    window.open(getConfig().apiDocsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className='space-y-lg'>
      <div className='flex justify-end gap-sm mb-md'>
        <Button
          icon='file-lines'
          variant='secondary'
          testId='api-keys-docs-btn'
          onClick={openApiDocs}
        >
          {t('actions.viewApiDocs')}
        </Button>
        <Button
          icon='plus'
          variant='primary'
          testId='api-keys-generate-btn'
          onClick={() => setShowGenerateModal(true)}
        >
          {t('actions.generateNewKey')}
        </Button>
      </div>

      <RolesGuide
        roles={KEY_ASSIGNABLE_ROLES}
        label={tRole}
        description={tRoleDescription}
        testId='api-keys-roles-guide'
      />

      <Paper showDivider={false} padding='none'>
        <ApiKeysTable
          keys={data?.apiKeysConnection?.edges?.map(e => e.node) ?? []}
          loading={showLoading}
          error={error}
          onRevoked={refetch}
          onEditRoles={setEditingKey}
        />
      </Paper>

      {showGenerateModal && (
        <GenerateApiKeyModal
          onClose={() => setShowGenerateModal(false)}
          onSuccess={handleGenerateSuccess}
        />
      )}

      {editingKey && (
        <EditApiKeyRolesModal
          key={editingKey.id}
          apiKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSuccess={handleEditRolesSuccess}
        />
      )}

      {generatedKey && (
        <ApiKeyDisplayModal
          key={generatedKey.id}
          apiKey={generatedKey}
          onClose={handleCloseGeneratedKeyModal}
        />
      )}
    </div>
  );
}
